import { useSyncExternalStore } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'farmos.theme';
const media =
  typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
const listeners = new Set<() => void>();

function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* storage unavailable */
  }
  return 'system';
}

let pref: ThemePref = read();

function apply() {
  const dark = resolved() === 'dark';
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? '#121327' : '#eceefa');
}

export function initTheme() {
  apply();
  media?.addEventListener('change', () => pref === 'system' && apply());
}

export function setTheme(next: ThemePref) {
  pref = next;
  try {
    if (next === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    /* storage unavailable */
  }
  apply();
  listeners.forEach((l) => l());
}

export function useTheme(): ThemePref {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => pref,
  );
}

function resolved(): 'light' | 'dark' {
  return pref === 'dark' || (pref === 'system' && !!media?.matches) ? 'dark' : 'light';
}

export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore((l) => {
    listeners.add(l);
    media?.addEventListener('change', l);
    return () => {
      listeners.delete(l);
      media?.removeEventListener('change', l);
    };
  }, resolved);
}
